import { UAE_EMIRATES } from "../constants/uae";

export const ORDER_SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "total_desc", label: "Total: high to low" },
  { value: "total_asc", label: "Total: low to high" },
  { value: "customer_asc", label: "Customer: A–Z" },
  { value: "order_number", label: "Order number" },
];

function getCustomerName(order) {
  const shipping = order.shippingAddress || {};
  return (shipping.fullName || order.user?.name || "").trim();
}

function getCustomerEmail(order) {
  const shipping = order.shippingAddress || {};
  return (shipping.email || order.user?.email || "").trim();
}

function getSearchHaystack(order) {
  const shipping = order.shippingAddress || {};
  return [
    order.orderNumber,
    getCustomerName(order),
    getCustomerEmail(order),
    shipping.phone,
    order.user?.phone,
    shipping.companyName,
    shipping.line1,
    shipping.line2,
    shipping.city,
    shipping.emirate,
    shipping.country,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function compareOrders(a, b, sortBy) {
  switch (sortBy) {
    case "oldest":
      return new Date(a.createdAt) - new Date(b.createdAt);
    case "total_desc":
      return Number(b.total || 0) - Number(a.total || 0);
    case "total_asc":
      return Number(a.total || 0) - Number(b.total || 0);
    case "customer_asc":
      return getCustomerName(a).localeCompare(getCustomerName(b), undefined, {
        sensitivity: "base",
      });
    case "order_number":
      return String(a.orderNumber || "").localeCompare(String(b.orderNumber || ""));
    case "newest":
    default:
      return new Date(b.createdAt) - new Date(a.createdAt);
  }
}

export const ORDER_PAGE_SIZES = [10, 20, 50, 100];

export function buildPageItems(current, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items = new Set([1, totalPages, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => items.add(n));
  if (current >= totalPages - 2) {
    [totalPages - 1, totalPages - 2, totalPages - 3].forEach((n) => items.add(n));
  }
  return [...items]
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b)
    .reduce((acc, n, i, arr) => {
      if (i > 0 && n - arr[i - 1] > 1) acc.push("…");
      acc.push(n);
      return acc;
    }, []);
}

export function paginateItems(items, page = 1, pageSize = 10) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = (safePage - 1) * pageSize;
  const end = Math.min(start + pageSize, total);

  return {
    items: items.slice(start, end),
    page: safePage,
    pageSize,
    total,
    totalPages,
    showingFrom: total === 0 ? 0 : start + 1,
    showingTo: end,
  };
}

export function filterAndSortOrders(orders, filters) {
  const {
    search = "",
    status = "all",
    emirate = "all",
    dateFrom = "",
    dateTo = "",
    sortBy = "newest",
  } = filters;

  const query = search.trim().toLowerCase();
  const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
  const toTime = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;

  const filtered = (orders || []).filter((order) => {
    if (status !== "all" && order.status !== status) return false;

    const shipping = order.shippingAddress || {};
    if (emirate !== "all" && shipping.emirate !== emirate) return false;

    if (fromTime || toTime) {
      const placed = new Date(order.createdAt).getTime();
      if (fromTime && placed < fromTime) return false;
      if (toTime && placed > toTime) return false;
    }

    if (query && !getSearchHaystack(order).includes(query)) return false;

    return true;
  });

  return [...filtered].sort((a, b) => compareOrders(a, b, sortBy));
}

export { UAE_EMIRATES as ORDER_EMIRATES };
